import { getSupabase } from '../supabaseClient';
import type { Todo, TodoEntity, StockItem } from '../../types/data';
import { toSnakeCase, toCamelCase } from '../../utils/dataTransform';
import { fetchAllPages } from './pagination';
import type { SyncCursor } from './syncCursor';
import { syncTodoChange, SyncRow, SyncResource } from './todoSync';
import { todoLocalRepo } from '../localDb/todoLocalRepo';

const persistTodoChange = async (resource: SyncResource, key: SyncRow, base: SyncRow | null, changes: SyncRow): Promise<SyncRow> => {
  const row = await syncTodoChange(resource, key, base, changes);
  await todoLocalRepo.applyServerRow(resource, row);
  return row;
};

const T_TODOS = 'todos';
const T_TAG_MAP = 'todo_tag_map';
const T_STOCK_ITEMS = 'todo_stock_items';
const IN_QUERY_BATCH_SIZE = 100;

type TagMapRow = { todo_id: string; tag_id: string };
export type TodoTagMapSyncRow = {
  todo_id: string;
  tag_id: string;
  created_at?: string;
  modified_at: string;
  is_deleted: boolean;
};
type MaxModifiedAtRow = { modified_at: string | null };
type StockItemRow = {
  id: string;
  todo_id: string;
  stock_id: string;
  is_checked?: boolean | null;
  to_buy?: number | null;
  created_at?: string;
  modified_at?: string;
  is_deleted: boolean;
};
export type TodoStockItemSyncRow = {
  id: string;
  todo_id: string;
  stock_id: string;
  is_checked: boolean;
  to_buy?: number | null;
  created_at?: string;
  modified_at: string;
  is_deleted: boolean;
};
export type TodoRelationMaxModifiedAts = {
  todoTagMap: string | null;
  todoStockItems: string | null;
};

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  if (items.length === 0) return [];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const getTableMaxModifiedAt = async (tableName: string): Promise<string | null> => {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from(tableName)
    .select('modified_at')
    .order('modified_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as MaxModifiedAtRow | null)?.modified_at ?? null;
};

export const todoRepo = {
  _isUuid(value: any): boolean {
    return typeof value === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(value);
  },
  async listTodos(): Promise<Todo[]> {
    const supabase = getSupabase();
    const todosEntities = await fetchAllPages<TodoEntity>(() =>
      supabase
        .from(T_TODOS)
        .select('*')
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
    );
    if (todosEntities.length === 0) return [];

    const ids = todosEntities.map(t => t.id);
    const idChunks = chunkArray(ids, IN_QUERY_BATCH_SIZE);

    // Tags
    const tagsByTodo = new Map<string, string[]>();
    for (const idChunk of idChunks) {
      const tagRows = await fetchAllPages<TagMapRow>(() =>
        supabase
          .from(T_TAG_MAP)
          .select('todo_id, tag_id')
          .in('todo_id', idChunk)
          .eq('is_deleted', false)
          .order('todo_id', { ascending: true })
          .order('tag_id', { ascending: true })
      );
      tagRows.forEach((r: TagMapRow) => {
        const arr = tagsByTodo.get(r.todo_id) || [];
        arr.push(r.tag_id);
        tagsByTodo.set(r.todo_id, arr);
      });
    }

    // Stock items
    const stockByTodo = new Map<string, StockItem[]>();
    const stockSeenByTodo = new Map<string, Set<string>>();
    for (const idChunk of idChunks) {
      const stockRows = await fetchAllPages<any>(() =>
        supabase
          .from(T_STOCK_ITEMS)
          .select('*')
          .in('todo_id', idChunk)
          .eq('is_deleted', false)
          .order('todo_id', { ascending: true })
          .order('stock_id', { ascending: true })
          .order('modified_at', { ascending: false })
          .order('id', { ascending: true })
      );
      stockRows.forEach((r: any) => {
        const todoId = r.todo_id as string;
        const stockId = r.stock_id as string;
        if (!todoId || !stockId) return;

        const seen = stockSeenByTodo.get(todoId) || new Set<string>();
        if (seen.has(stockId)) return;
        seen.add(stockId);
        stockSeenByTodo.set(todoId, seen);

        const arr = stockByTodo.get(todoId) || [];
        const item: StockItem = { stock_id: stockId, is_checked: !!r.is_checked, to_buy: r.to_buy ?? undefined };
        arr.push(item);
        stockByTodo.set(todoId, arr);
      });
    }

    // Compose UI todos
    const todos: Todo[] = todosEntities.map(e => {
      const ui = toCamelCase(e);
      ui.todoTagIds = tagsByTodo.get(e.id) || [];
      ui.stockItems = stockByTodo.get(e.id) || [];
      return ui;
    });
    return todos;
  },

  async listTodosModifiedAfter(cursor: SyncCursor): Promise<Todo[]> {
    const supabase = getSupabase();
    const todosEntities = await fetchAllPages<TodoEntity>(() =>
      applyModifiedAtIdCursor(
        supabase
        .from(T_TODOS)
        .select('*'),
        cursor
      )
        .order('modified_at', { ascending: true })
        .order('id', { ascending: true })
    );
    if (todosEntities.length === 0) return [];

    const ids = todosEntities.map(t => t.id);
    const idChunks = chunkArray(ids, IN_QUERY_BATCH_SIZE);

    const tagsByTodo = new Map<string, string[]>();
    for (const idChunk of idChunks) {
      const tagRows = await fetchAllPages<TagMapRow>(() =>
        supabase
          .from(T_TAG_MAP)
          .select('todo_id, tag_id')
          .in('todo_id', idChunk)
          .eq('is_deleted', false)
          .order('todo_id', { ascending: true })
          .order('tag_id', { ascending: true })
      );
      tagRows.forEach((r: TagMapRow) => {
        const arr = tagsByTodo.get(r.todo_id) || [];
        arr.push(r.tag_id);
        tagsByTodo.set(r.todo_id, arr);
      });
    }

    const stockByTodo = new Map<string, StockItem[]>();
    const stockSeenByTodo = new Map<string, Set<string>>();
    for (const idChunk of idChunks) {
      const stockRows = await fetchAllPages<StockItemRow>(() =>
        supabase
          .from(T_STOCK_ITEMS)
          .select('*')
          .in('todo_id', idChunk)
          .eq('is_deleted', false)
          .order('todo_id', { ascending: true })
          .order('stock_id', { ascending: true })
          .order('modified_at', { ascending: false })
          .order('id', { ascending: true })
      );
      stockRows.forEach((r: StockItemRow) => {
        const todoId = r.todo_id;
        const stockId = r.stock_id;
        if (!todoId || !stockId) return;

        const seen = stockSeenByTodo.get(todoId) || new Set<string>();
        if (seen.has(stockId)) return;
        seen.add(stockId);
        stockSeenByTodo.set(todoId, seen);

        const arr = stockByTodo.get(todoId) || [];
        const item: StockItem = { stock_id: stockId, is_checked: !!r.is_checked, to_buy: r.to_buy ?? undefined };
        arr.push(item);
        stockByTodo.set(todoId, arr);
      });
    }

    return todosEntities.map(e => {
      const ui = toCamelCase(e);
      ui.todoTagIds = tagsByTodo.get(e.id) || [];
      ui.stockItems = stockByTodo.get(e.id) || [];
      return ui;
    });
  },

  async listTodoTagMapModifiedAfter(cursor: SyncCursor): Promise<TodoTagMapSyncRow[]> {
    const supabase = getSupabase();
    return await fetchAllPages<TodoTagMapSyncRow>(() =>
      applyTodoTagMapCursor(
        supabase
        .from(T_TAG_MAP)
        .select('todo_id, tag_id, created_at, modified_at, is_deleted'),
        cursor
      )
        .order('modified_at', { ascending: true })
        .order('todo_id', { ascending: true })
        .order('tag_id', { ascending: true })
    );
  },

  async listTodoTagMapRows(): Promise<TodoTagMapSyncRow[]> {
    const supabase = getSupabase();
    return await fetchAllPages<TodoTagMapSyncRow>(() =>
      supabase
        .from(T_TAG_MAP)
        .select('todo_id, tag_id, created_at, modified_at, is_deleted')
        .order('modified_at', { ascending: true })
        .order('todo_id', { ascending: true })
        .order('tag_id', { ascending: true })
    );
  },

  async listTodoStockItemsModifiedAfter(cursor: SyncCursor): Promise<TodoStockItemSyncRow[]> {
    const supabase = getSupabase();
    return await fetchAllPages<TodoStockItemSyncRow>(() =>
      applyModifiedAtIdCursor(
        supabase
        .from(T_STOCK_ITEMS)
        .select('id, todo_id, stock_id, is_checked, to_buy, created_at, modified_at, is_deleted'),
        cursor
      )
        .order('modified_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async listTodoStockItemRows(): Promise<TodoStockItemSyncRow[]> {
    const supabase = getSupabase();
    return await fetchAllPages<TodoStockItemSyncRow>(() =>
      supabase
        .from(T_STOCK_ITEMS)
        .select('id, todo_id, stock_id, is_checked, to_buy, created_at, modified_at, is_deleted')
        .order('modified_at', { ascending: true })
        .order('id', { ascending: true })
    );
  },

  async getTodoRelationMaxModifiedAts(): Promise<TodoRelationMaxModifiedAts> {
    const [todoTagMap, todoStockItems] = await Promise.all([
      getTableMaxModifiedAt(T_TAG_MAP),
      getTableMaxModifiedAt(T_STOCK_ITEMS),
    ]);

    return { todoTagMap, todoStockItems };
  },

  async createTodo(input: Todo): Promise<Todo> {
    const id = input.id || crypto.randomUUID();
    const entity = toSnakeCase({ ...input, id });
    const fields = ['title', 'body', 'is_done', 'is_canceled', 'is_deleted', 'start_at', 'done_at', 'habit_id'] as const;
    const changes: SyncRow = {};
    fields.forEach(field => { changes[field] = entity[field] ?? null; });
    if (changes.habit_id && !this._isUuid(changes.habit_id)) changes.habit_id = null;
    const row = await persistTodoChange('todos', { id }, null, changes);
    await this.setTodoTags(id, input.todoTagIds ?? [], []);
    await this.setTodoStockItems(id, input.stockItems ?? [], []);
    return { ...toCamelCase(row as unknown as TodoEntity), todoTagIds: input.todoTagIds ?? [], stockItems: input.stockItems ?? [] };
  },

  async updateTodo(id: string, patch: Partial<Todo>, original: Todo): Promise<Todo> {
    const mapping = { title: 'title', body: 'body', done: 'is_done', canceled: 'is_canceled', deleted: 'is_deleted', startAt: 'start_at', doneAt: 'done_at', habitId: 'habit_id' } as const;
    const base = toSnakeCase(original) as unknown as SyncRow;
    const changes: SyncRow = {};
    (Object.keys(mapping) as Array<keyof typeof mapping>).forEach(field => {
      if (Object.prototype.hasOwnProperty.call(patch, field) && (patch[field] ?? null) !== (original[field] ?? null)) {
        changes[mapping[field]] = patch[field] ?? null;
      }
    });
    const row = Object.keys(changes).length ? await persistTodoChange('todos', { id }, base, changes) : base;
    if (patch.todoTagIds) await this.setTodoTags(id, patch.todoTagIds, original.todoTagIds ?? []);
    if (patch.stockItems) await this.setTodoStockItems(id, patch.stockItems, original.stockItems ?? []);
    return { ...toCamelCase(row as unknown as TodoEntity), todoTagIds: patch.todoTagIds ?? original.todoTagIds, stockItems: patch.stockItems ?? original.stockItems };
  },

  async setTodoTags(todoId: string, tagIds: string[], original: string[] = []): Promise<void> {
    const before = new Set(original);
    const after = new Set(tagIds);
    for (const tagId of Array.from(new Set([...original, ...tagIds]))) {
      if (before.has(tagId) === after.has(tagId)) continue;
      await persistTodoChange('todo_tag_map', { todo_id: todoId, tag_id: tagId },
        before.has(tagId) ? { is_deleted: false } : null, { is_deleted: !after.has(tagId) });
    }
  },

  async setTodoStockItems(todoId: string, items: StockItem[], original: StockItem[] = []): Promise<void> {
    const before = new Map(original.map(item => [item.stock_id, item]));
    const after = new Map(items.map(item => [item.stock_id, item]));
    for (const stockId of Array.from(new Set([...Array.from(before.keys()), ...Array.from(after.keys())]))) {
      const previous = before.get(stockId);
      const next = after.get(stockId);
      const base: SyncRow | null = previous ? { is_checked: previous.is_checked, to_buy: previous.to_buy ?? 0, is_deleted: false } : null;
      const desired: SyncRow = next ? { is_checked: next.is_checked, to_buy: next.to_buy ?? 0, is_deleted: false } : { is_deleted: true };
      const changes = Object.fromEntries(Object.entries(desired).filter(([field, value]) => base?.[field] !== value));
      if (Object.keys(changes).length) await persistTodoChange('todo_stock_items', { todo_id: todoId, stock_id: stockId }, base, changes);
    }
  },

};

const applyModifiedAtIdCursor = (query: any, cursor: SyncCursor): any => {
  const id = cursor.tieBreaker[0];
  if (!id) return query.gte('modified_at', cursor.modifiedAt);

  return query.or(`modified_at.gt.${cursor.modifiedAt},and(modified_at.eq.${cursor.modifiedAt},id.gt.${id})`);
};

const applyTodoTagMapCursor = (query: any, cursor: SyncCursor): any => {
  const [todoId, tagId] = cursor.tieBreaker;
  if (!todoId || !tagId) return query.gte('modified_at', cursor.modifiedAt);

  return query.or(
    [
      `modified_at.gt.${cursor.modifiedAt}`,
      `and(modified_at.eq.${cursor.modifiedAt},todo_id.gt.${todoId})`,
      `and(modified_at.eq.${cursor.modifiedAt},todo_id.eq.${todoId},tag_id.gt.${tagId})`,
    ].join(',')
  );
};
